import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '@/domains/auth/hooks/useAuth';
import { useUserManagement } from '@/domains/system-management/hooks/useUserManagement';
import Header from '@/shared/components/layout/Header/Header';
import Sidebar from '@/shared/components/layout/Sidebar/Sidebar';
import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import { usePreferenceContext } from '@/shared/context/preferenceContext';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { fetchTranslation } from '@/shared/utils/i18n';
import { isRouteAuthorized, UNAUTHORIZED_ROUTE } from '@/shared/utils/navigation';
import { useAuthStore } from '@/store/authStore';
import { useLoadingStore } from '@/store/loadingStore';

export interface TitleMap {
  eng: string;
  // kor: string;
  ind: string;
}

const ProtectedRoutes = (): JSX.Element | null => {
  const location = useLocation();
  const navigate = useNavigate();
  const { preferenceData } = usePreferenceContext();
  const { i18n } = useTranslation();
  const { isLogin, user_id, lang: storedLang, account_type } = useAuthStore();
  const { useUser } = useUserManagement();
  const { data: userData } = useUser(user_id);

  const { handleAuthority } = useAuth({
    userID: user_id || null,
  });

  const [optionParams] = useState<string>('upper-code=021&upper-code=ORG');
  const { commonListData } = useCommonCodes({ optionParams });

  useEffect(() => {
    if (!isLogin) {
      navigate('/', { replace: true });
    }
  }, [isLogin, navigate]);

  // A healthcare account is confined to the PSC dashboard cluster - it has no
  // legitimate reason to reach system-management/asset-management/smart-city
  // pages, and the backend enforces the same boundary on its own endpoints
  // (see api-dsh's `internal_only`), so this is UX, not the security
  // boundary itself. /regis-dashboard (the Avaya/WhatsApp chat view) is
  // deliberately excluded - a healthcare account never sees those channels.
  useEffect(() => {
    if (!isLogin || account_type !== 'healthcare') return;
    const allowed = [
      '/dashboard/psc-dashboard',
      '/dashboard/ambulance-dashboard',
      '/dashboard/details-dashboard',
      '/report-management/healthcare-report',
    ];
    if (!allowed.some((path) => location.pathname.startsWith(path))) {
      navigate('/dashboard/psc-dashboard', { replace: true });
    }
  }, [isLogin, account_type, location.pathname, navigate]);

  // A page opens only when the authority list covers the tab/section that owns
  // it, so a url typed by hand lands on the unauthorized page instead of a
  // screen the user holds no grants for. Healthcare accounts are already
  // confined to the PSC cluster above, and their rule is the stricter one.
  useEffect(() => {
    if (!isLogin || account_type === 'healthcare') return;
    if (isRouteAuthorized(location.pathname, handleAuthority)) return;
    navigate(UNAUTHORIZED_ROUTE, { replace: true });
  }, [isLogin, account_type, handleAuthority, location.pathname, navigate]);

  useEffect(() => {
    const initLanguage = async () => {
      const lang = storedLang || 'eng';

      const hasResource = i18n.hasResourceBundle(lang, 'translation');
      if (hasResource) {
        i18n.changeLanguage(lang);
        return;
      }

      const cached = localStorage.getItem(`translation_${lang}`);
      if (cached) {
        try {
          i18n.addResourceBundle(lang, 'translation', JSON.parse(cached), true, true);
          i18n.changeLanguage(lang);
          // Kamus cache bisa basi (tag baru di cm_language tidak pernah terbawa
          // tanpa logout); segarkan di background tanpa loader.
          fetchTranslation(lang);
          return;
        } catch {
          localStorage.removeItem(`translation_${lang}`);
        }
      }

      // Jalur lambat (fetch kamus dari backend saat state awal): tampilkan
      // loader global. Jalur cache di atas instan, tanpa kedip loader.
      const { setTranslationLoading } = useLoadingStore.getState();
      setTranslationLoading(true);
      try {
        await fetchTranslation(lang);
        await i18n.changeLanguage(lang);
      } finally {
        setTranslationLoading(false);
      }
    };

    initLanguage();
  }, [i18n, storedLang]);

  const titleMap: TitleMap = {
    eng: 'Smart Safety',
    // kor: 'Smart Safety',
    ind: 'Smart Safety',
  };
  const titleDashboard: TitleMap = {
    eng: 'Smart Safety',
    // kor: 'Smart Safety',
    ind: 'Smart Safety',
  };

  const isDashboard = location.pathname.startsWith('/dashboard');

  useEffect(() => {
    // eng | ind only; anything else (leftover 'kor' session) falls back to eng
    const lang: keyof TitleMap = i18n.language === 'eng' ? 'ind' : 'eng';
    document.title = isDashboard ? titleDashboard[lang] : titleMap[lang];
  }, [i18n.language, isDashboard]);

  if (!userData || !preferenceData) {
    return null;
  }

  if (isDashboard) {
    return <Outlet context={{ userInfo: userData, preferenceData: preferenceData }} />;
  }

  return (
    <div className="h-screen flex flex-col wrap-management">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          userInfo={userData}
          commonData={commonListData}
          navbarList={handleAuthority}
          preferenceData={preferenceData}
        />

        <main className="flex-1 overflow-y-auto p-10 bg-neutral-50 dark:bg-[#191d23]!">
          <Outlet context={{ userInfo: userData, preferenceData }} />
        </main>
      </div>
    </div>
  );
};

export default ProtectedRoutes;
